import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '@/contexts/AdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import logger from '@/utils/logger';
import { 
  Activity, 
  User, 
  UserPlus, 
  UserMinus, 
  UserCog, 
  FileText, 
  RefreshCw,
  Calendar,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_ICONS = {
  created_user: UserPlus,
  updated_user: UserCog,
  deleted_user: UserMinus,
  login: User,
  pdf_generated: FileText,
  default: Activity
};

const ACTION_COLORS = {
  created_user: 'bg-green-100 text-green-600',
  updated_user: 'bg-blue-100 text-blue-600',
  deleted_user: 'bg-red-100 text-red-600',
  login: 'bg-purple-100 text-purple-600',
  pdf_generated: 'bg-amber-100 text-amber-600',
  default: 'bg-slate-100 text-slate-600'
};

const AdminActivityLogs = () => {
  const { getAuthHeaders } = useAdmin();
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [selectedUser, setSelectedUser] = useState('all');

  useEffect(() => {
    fetchData();
  }, [selectedUser]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = selectedUser !== 'all' ? `?admin_id=${selectedUser}` : '';
      
      const [logsRes, usersRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/activity-logs${params}`, {
          headers: getAuthHeaders()
        }),
        axios.get(`${API_URL}/api/admin/users`, {
          headers: getAuthHeaders()
        }),
        axios.get(`${API_URL}/api/admin/activity-logs/summary`, {
          headers: getAuthHeaders()
        })
      ]);
      
      setLogs(logsRes.data);
      setUsers(usersRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      logger.error('Error fetching activity logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionIcon = (action) => {
    const Icon = ACTION_ICONS[action] || ACTION_ICONS.default;
    return Icon;
  };

  const getActionColor = (action) => {
    return ACTION_COLORS[action] || ACTION_COLORS.default;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="activity-logs">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-slate-800">Activity Logs</h1>
          <p className="text-sm lg:text-base text-slate-500">Monitor all admin user activities</p>
        </div>
        <Button
          variant="outline"
          onClick={fetchData}
          className="gap-2 w-full sm:w-auto"
          data-testid="refresh-logs-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4">
        <Card>
          <CardContent className="p-3 lg:p-4">
            <div className="flex items-center gap-3 lg:gap-4">
              <div className="p-2 lg:p-3 bg-blue-100 rounded-lg">
                <Activity className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-lg lg:text-2xl font-bold text-slate-800">{summary?.today_activities || 0}</p>
                <p className="text-xs lg:text-sm text-slate-500">Today&apos;s Activities</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 lg:p-4">
            <div className="flex items-center gap-3 lg:gap-4">
              <div className="p-2 lg:p-3 bg-green-100 rounded-lg">
                <FileText className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" />
              </div>
              <div>
                <p className="text-lg lg:text-2xl font-bold text-slate-800">{summary?.total_activities || 0}</p>
                <p className="text-xs lg:text-sm text-slate-500">Total Activities</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 lg:p-4">
            <div className="flex items-center gap-3 lg:gap-4">
              <div className="p-2 lg:p-3 bg-purple-100 rounded-lg">
                <User className="w-5 h-5 lg:w-6 lg:h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-lg lg:text-2xl font-bold text-slate-800 truncate max-w-[120px]">{summary?.most_active_admin || '-'}</p>
                <p className="text-xs lg:text-sm text-slate-500">Most Active Admin</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-3 lg:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 lg:gap-4">
            <span className="text-sm font-medium text-slate-600">Filter by Admin:</span>
            <Select value={selectedUser} onValueChange={setSelectedUser}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="filter-user">
                <SelectValue placeholder="All Users" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                {users.map(user => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Activity Log List */}
      <Card>
        <CardHeader className="p-4 lg:p-6">
          <CardTitle className="text-base lg:text-lg">Recent Activities</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {logs.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Activity className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No activity logs found</p>
            </div>
          ) : (
            <div className="divide-y">
              {logs.map((log) => {
                const ActionIcon = getActionIcon(log.action);
                return (
                  <div 
                    key={log.id || log._id || `${log.timestamp}-${log.action}`} 
                    className="p-3 lg:p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`log-entry-${log.id || log._id}`}
                  >
                    <div className="flex items-start gap-3 lg:gap-4">
                      <div className={`p-1.5 lg:p-2 rounded-lg ${getActionColor(log.action)} flex-shrink-0`}>
                        <ActionIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
                          <span className="font-medium text-slate-800 text-sm lg:text-base">{log.admin_name}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 w-fit">
                            {log.action.replace('_', ' ')}
                          </span>
                        </div>
                        <p className="text-xs lg:text-sm text-slate-600 break-words">{log.details}</p>
                        {log.target_user && (
                          <p className="text-xs text-slate-500 mt-1">
                            Target: {log.target_user}
                          </p>
                        )}
                        {/* Mobile timestamp */}
                        <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 sm:hidden">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(log.timestamp)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTime(log.timestamp)}
                          </span>
                        </div>
                      </div>
                      {/* Desktop timestamp */}
                      <div className="hidden sm:block text-right text-sm text-slate-500 shrink-0">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(log.timestamp)}
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <Clock className="w-3 h-3" />
                          {formatTime(log.timestamp)}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminActivityLogs;
